import { config } from '../../../../services/Constants/AppConstants';
import {
  EMembershipGroup,
  EMembershipPlatform,
  EMembershipStatus,
  EMigrationStrategy,
} from '../types';

const MONTHLY_ID = config.chargebeePlans.MONTHLY;
const QUARTERLY_ID = config.chargebeePlans.QUARTERLY;
const BIANNUAL_ID = config.chargebeePlans.BIANNUAL;
const ANNUAL_ID = config.chargebeePlans.ANNUAL;

export const getDateTitle = (status?: EMembershipStatus) => {
  switch (status) {
    case EMembershipStatus.trial:
      return 'first payment date';
    case EMembershipStatus.active:
      return 'renewal date';
    case EMembershipStatus.expired:
    case EMembershipStatus.cancelled:
      return 'expiration date';
    default:
      return 'expiration date';
  }
};

export const getStatusState = (status?: EMembershipStatus) => {
  switch (status) {
    case EMembershipStatus.trial:
    case EMembershipStatus.active:
      return 'active';
    case EMembershipStatus.expired:
    case EMembershipStatus.cancelled:
      return 'inactive';
    default:
      return 'active';
  }
};

export const getMethods = (
  manageSubscription: () => Promise<void>,
  manageUhcSubscription: () => Promise<void>,
  upgradeCBSubscription: (id: string) => Promise<void>,
  upgradePmproSubscription: () => void,
  upgradePayPalSubscription: (chargebeePlanId?: string) => Promise<void>,
  uncancelPayPalSubscription: () => Promise<void>,
  cancel: () => Promise<void>,
  startSubscription: () => void,
  uncancelSubscription: () => Promise<void>,
  migratePmpro: (
    strategy: EMigrationStrategy,
    chargebeePlanId?: string | null,
    withPortal?: boolean,
  ) => Promise<void>,
) => {
  const methods: { [key: string]: { func: () => void | Promise<void>; linkName: string } } = {
    start: {
      func: () => startSubscription(),
      linkName: 'Choose a membership level',
    },
    manage: {
      func: () => manageSubscription(),
      linkName: 'manage',
    },
    monthly: {
      func: () => upgradeCBSubscription(MONTHLY_ID),
      linkName: 'upgrade to monthly',
    },
    quarterly: {
      func: () => upgradeCBSubscription(QUARTERLY_ID),
      linkName: 'upgrade to quarterly',
    },
    month6: {
      func: () => upgradeCBSubscription(BIANNUAL_ID),
      linkName: 'upgrade to biannual',
    },
    annual: {
      func: () => upgradeCBSubscription(ANNUAL_ID),
      linkName: 'upgrade to annual',
    },
    cancel: {
      func: () => cancel(),
      linkName: 'cancel',
    },
    uncancel: {
      func: () => uncancelSubscription(),
      linkName: 'uncancel',
    },
    upgrade: {
      func: () => upgradePmproSubscription(),
      linkName: 'upgrade plan',
    },
    uncancelBraintree: {
      func: () => migratePmpro(EMigrationStrategy.DURING_UNCANCEL, null, false),
      linkName: 'uncancel',
    },
    quarterlyBraintree: {
      func: () => migratePmpro(EMigrationStrategy.DURING_UPGRADE, QUARTERLY_ID),
      linkName: 'upgrade to quarterly',
    },
    month6Braintree: {
      func: () => migratePmpro(EMigrationStrategy.DURING_UPGRADE, BIANNUAL_ID),
      linkName: 'upgrade to biannual',
    },
    annualBraintree: {
      func: () => migratePmpro(EMigrationStrategy.DURING_UPGRADE, ANNUAL_ID),
      linkName: 'upgrade to annual',
    },
    quarterlyPayPal: {
      func: () => upgradePayPalSubscription(QUARTERLY_ID),
      linkName: 'upgrade to quarterly',
    },
    month6PayPal: {
      func: () => upgradePayPalSubscription(BIANNUAL_ID),
      linkName: 'upgrade to biannual',
    },
    annualPayPal: {
      func: () => upgradePayPalSubscription(ANNUAL_ID),
      linkName: 'upgrade to annual',
    },
    uncancelPayPal: {
      func: () => uncancelPayPalSubscription(),
      linkName: 'uncancel',
    },
    manageUhcSubscription: {
      func: () => manageUhcSubscription(),
      linkName: 'manage UHC subscription',
    },
  };

  return methods;
};

export const getLinksForChargebee = (
  billingPeriodUnit: string,
  billingPeriod: number,
  group: EMembershipGroup,
) => {
  const consumerActiveStatusLinks: Record<string, Array<string>> = {
    day: ['monthly', 'quarterly', 'month6', 'annual', 'manage', 'cancel'],
    month1: ['quarterly', 'month6', 'annual', 'manage', 'cancel'],
    month3: ['month6', 'annual', 'manage', 'cancel'],
    month6: ['annual', 'manage', 'cancel'],
    year: ['manage', 'cancel'],
  };

  const billingKey = `${billingPeriodUnit}${billingPeriodUnit === 'month' ? billingPeriod : ''}`;

  const consumerLinks: Record<string, Array<string>> = {
    [EMembershipStatus.trial]:
      consumerActiveStatusLinks[billingKey] ?? consumerActiveStatusLinks.month1,
    [EMembershipStatus.active]: consumerActiveStatusLinks[billingKey],
    [EMembershipStatus.expired]: ['start'],
    [EMembershipStatus.cancelled]: ['manage', 'uncancel'],
  };

  const enterpriseLinks: Record<string, Array<string>> = {
    [EMembershipStatus.expired]: ['start'],
  };

  return group === EMembershipGroup.consumer ? consumerLinks : enterpriseLinks;
};

export const getLinksForFlex = () => {
  const consumerLinks: Record<string, Array<string>> = {
    [EMembershipStatus.trial]: ['cancel'],
    [EMembershipStatus.active]: ['cancel'],
    [EMembershipStatus.expired]: ['start'],
    [EMembershipStatus.past_due]: ['cancel'],
    [EMembershipStatus.cancelled]: ['uncancel'],
  };

  return consumerLinks;
};

export const getLinksForPmpro = (
  billingPeriodUnit: string,
  billingPeriod: number,
  group: EMembershipGroup,
) => {
  const consumerActiveStatusLinks: Record<string, Array<string>> = {
    month: ['upgrade', 'cancel'],
    year: ['cancel'],
  };

  const consumerLinks: Record<string, Array<string>> = {
    [EMembershipStatus.trial]: ['cancel'],
    [EMembershipStatus.active]: consumerActiveStatusLinks[billingPeriodUnit],
    [EMembershipStatus.expired]: ['start'],
  };

  const migrationBraintreeActiveStatusLinks: Record<string, Array<string>> = {
    month1: ['quarterlyBraintree', 'month6Braintree', 'annualBraintree', 'cancel'],
    month3: ['month6Braintree', 'annualBraintree', 'cancel'],
    year: ['cancel'],
  };

  const migrationBraintreeLinks: Record<string, Array<string>> = {
    ...consumerLinks,
    [EMembershipStatus.active]:
      migrationBraintreeActiveStatusLinks[
        `${billingPeriodUnit}${billingPeriodUnit === 'month' ? billingPeriod : ''}`
      ],
    [EMembershipStatus.cancelled]: ['uncancelBraintree'],
  };

  const migrationPayPalActiveStatusLinks: Record<string, Array<string>> = {
    month1: ['quarterlyPayPal', 'month6PayPal', 'annualPayPal', 'cancel'],
    month3: ['month6PayPal', 'annualPayPal', 'cancel'],
    year: ['cancel'],
  };

  const migrationPayPalLinks: Record<string, Array<string>> = {
    ...consumerLinks,
    [EMembershipStatus.active]:
      migrationPayPalActiveStatusLinks[
        `${billingPeriodUnit}${billingPeriodUnit === 'month' ? billingPeriod : ''}`
      ],
    [EMembershipStatus.cancelled]: ['uncancelPayPal'],
  };

  if (group === EMembershipGroup.consumer) {
    return consumerLinks;
  }
  if ([EMembershipGroup.migration_braintree, EMembershipGroup.migration_free].includes(group)) {
    return migrationBraintreeLinks;
  }

  if (group === EMembershipGroup.migration_paypal) {
    return migrationPayPalLinks;
  }
  return { [EMembershipStatus.expired]: ['start'] };
};

export const getLinksForMobile = () => {
  const links: Record<string, Array<string>> = {
    [EMembershipStatus.expired]: ['start'],
  };

  return links;
};

export const getLinksForUHC = () => {
  const links: Record<string, Array<string>> = {
    [EMembershipStatus.active]: ['manageUhcSubscription'],
  };

  return links;
};

export const getNameByGroup = (
  group?: EMembershipGroup,
  name?: string,
  status?: EMembershipStatus,
  platform?: EMembershipPlatform,
) => {
  if (!name || status === EMembershipStatus.expired) {
    return 'You do not have an active membership';
  }

  if (group === EMembershipGroup.iap) {
    return `${name} IOS In-App Subscription`;
  }
  if (group === EMembershipGroup.iab) {
    return `${name} Android In-App Subscription`;
  }
  if (group === EMembershipGroup.enterprise && platform === EMembershipPlatform.uhc) {
    return `${name} UHC Subscription`;
  }

  return name;
};

export const getStatusFormatted = (status?: EMembershipStatus) => {
  switch (status) {
    case EMembershipStatus.past_due:
      return 'Past Due';
    case EMembershipStatus.active:
      return 'Active';
    case EMembershipStatus.trial:
      return 'Trial';
    case EMembershipStatus.cancelled:
      return 'Cancelled';
    case EMembershipStatus.expired:
      return 'Expired';
    default:
      return status;
  }
};
